import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Tooltip } from "antd";
import InformationCircleIcon from "@/icons/InformationCircleIcon";

const InfoCircleTooltip = ({
  children,
  className,
  style,
  open, // если передан — контролируемый режим
  defaultOpen,
  onOpenChange,

  hoverable = true,
  clickable = true,
  disabled = false,

  flashKey,
  flashDuration = 5000,

  placement = "top",
  size = 18,
  iconColor = "#BBBBBB",
  badgeBg = "transparent",
  badgeHoverBg = "#F2F2F2",
  ariaLabel = "Показать подсказку",
}) => {
  // локальные флаги
  const [hovered, setHovered] = useState(false);
  const [clicked, setClicked] = useState(!!defaultOpen);
  const [flashing, setFlashing] = useState(false);
  const flashTimer = useRef(null);
  const rootRef = useRef(null);

  // Если компонент контролируемый, то внутреннюю логику сводим к вычислению
  const isControlled = typeof open === "boolean";

  // Композитная логика открытия, если НЕ контролируемо
  const computedOpen = isControlled
    ? open
    : !!(hovered || clicked || flashing);

  // Пробрасываем изменение наружу (если нужно)
  const setOpen = useCallback(
    (next) => {
      if (!isControlled) {
        // в неконтролируемом режиме управляем кликовым флагом
        setClicked(next);
        if (!next) {
          // если закрываем насильно — убираем и hover/flashing
          setHovered(false);
          setFlashing(false);
        }
      }
      onOpenChange?.(next);
    },
    [isControlled, onOpenChange]
  );

  // Hover handlers
  const handleMouseEnter = useCallback(() => {
    if (disabled || !hoverable) return;
    setHovered(true);
  }, [disabled, hoverable]);

  const handleMouseLeave = useCallback(() => {
    if (disabled || !hoverable) return;
    setHovered(false);
  }, [disabled, hoverable]);

  // Click toggle (фиксация)
  const handleClick = useCallback(
    (e) => {
      if (disabled || !clickable) return;
      e.stopPropagation();
      const next = !computedOpen || !clicked; // если открыт только hover/flash — зафиксируем; если уже «кликнут» — снимем фиксацию
      setClicked(next);
      if (!next && !hoverable) {
        // если ховера нет, при закрытии уберём всё
        setHovered(false);
        setFlashing(false);
      }
      onOpenChange?.(next);
    },
    [disabled, clickable, computedOpen, clicked, hoverable, onOpenChange]
  );

  // Закрытие при клике вне
  useEffect(() => {
    if (!clickable) return;
    const onDocClick = (e) => {
      if (!rootRef.current) return;
      if (rootRef.current.contains(e.target)) return;
      // снимаем кликовую фиксацию
      if (!isControlled) setClicked(false);
      onOpenChange?.(false);
    };
    if (clicked) {
      document.addEventListener("mousedown", onDocClick);
    }
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [clicked, clickable, isControlled, onOpenChange]);

  // «Вспышка» по внешнему событию
  useEffect(() => {
    if (disabled) return;
    if (typeof flashKey === "undefined") return;

    // каждый раз при изменении flashKey показываем тултип на flashDuration мс
    setFlashing(true);
    if (flashTimer.current) window.clearTimeout(flashTimer.current);
    flashTimer.current = window.setTimeout(() => {
      setFlashing(false);
    }, Math.max(0, flashDuration));
    return () => {
      if (flashTimer.current) window.clearTimeout(flashTimer.current);
    };
  }, [flashKey, flashDuration, disabled]);

  // Клавиатурная доступность: Enter/Space — переключение
  const onKeyDown = useCallback(
    (e) => {
      if (disabled) return;
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        handleClick(e);
      }
      if (e.key === "Escape") {
        setOpen(false);
      }
    },
    [disabled, handleClick, setOpen]
  );

  const buttonStyles = useMemo(
    () => ({
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      width: size,
      height: size,
      borderRadius: "50%",
      background: hovered ? badgeHoverBg : badgeBg,
      cursor: disabled ? "not-allowed" : "pointer",
      transition: "background 120ms ease",
      border: 0,
      padding: 0,
      lineHeight: 0,
      ...style,
    }),
    [size, hovered, badgeHoverBg, badgeBg, disabled, style]
  );

  return (
    <Tooltip
      placement={placement}
      open={computedOpen}
      // полностью отключаем встроенные триггеры, управляем сами
      trigger={[]}
      title={children}
    >
      <button
        ref={rootRef}
        type="button"
        aria-label={ariaLabel}
        className={className}
        style={buttonStyles}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onClick={handleClick}
        onKeyDown={onKeyDown}
        disabled={disabled}
      >
        <InformationCircleIcon
          style={{ fontSize: size, color: iconColor, display: "block" }}
        />
      </button>
    </Tooltip>
  );
};

export default InfoCircleTooltip;
